import axios, { AxiosProgressEvent } from 'axios';
import { call, put, select, take, takeLatest } from 'redux-saga/effects';

import { ConfigurationService } from './configuration.service';
import { configurationActions } from './configuration.slice';
import { ApiResult, FileDetails, IAsyncData } from 'common/models';
import { PayloadAction } from '@reduxjs/toolkit';
import {
    DELETE_POPUP_MSG,
    DELETE_POPUP_SUCCESS,
    FAILURE_HEADER,
    DELETE_POPUP_FAILURE,
    SUCCESS_HEADER,
    FILE_UPLOAD_SUCCESS_MESSAGE,
    FILE_UPLOAD_ERROR_MESSAGE,
    INDICATOR_SOURCE_ADD_FAIL,
    INDICATOR_SOURCE_ADD_SUCCESS,
    INDICATOR_SOURCE_EDIT_FAIL,
    INDICATOR_SOURCE_EDIT_SUCCESS,
    INDICATOR_SOURCE_DELETE_FAIL,
    INDICATOR_SOURCE_DELETE_SUCCESS,
    PAGE_NUMBER_DEFAULT,
    PAGE_SIZE_DEFAULT,
    DEFAULT_SAVE_MESSAGE,
    DEFAULT_FAILURE_SAVE_MESSAGE
} from 'common/config';
import { EntityType, MessageType } from 'common/enums';
import { ConfigurationTypes, WhatsNewLinkType } from '../enums/configuration.enum';
import { appStateActions } from 'common/redux/app-state';
import { RootState } from 'common/redux/core/root.reducer';
import {
    ConfigurationDetails,
    ConfigurationIndicatorVM,
    ConfigurationListRequest,
    FileUploadLogDataVM,
    DeleteConfigRequestType,
    ReorderTrendingDataPayloadType,
    HierarchicalExpansionPayload,
    ConfigurationDataModifier,
    ConfigurationSaveRequest,
    TrendingDataSaveRequest,
    DeleteConfigurationRequest,
    ConfigurationWhatsNewVM,
    ConfigurationWhatsNewDeleteRequest,
    WhatsNewIndicatorSaveRequest,
    ConfigurationReorderUpdateList,
    ConfigurationSaveLinkRequest,
    ConfigurationUploadFileRequest,
    IndicatorSourceDTO,
    VideoDetailsPayload,
    DeleteVideoLinkPayload,
    VideoDetailResponse
} from '../models/configuration.model';
import { PaginatedApiResponse } from 'common/models/api-service/api-response.model';
import { IErrorApiResponse } from 'common/models/api-service/error-response.model';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { Entity, EntityVM, MasterDataEntity } from 'common/models/data/entity.model';
import { findCategories, findCategoriesByHierarchyId, mapIndicators } from 'common/utils/table';
import { hasValue } from 'common/utils/data';
import {
    getFrequentlyUsedResponse,
    getGeographyOverviewResponse,
    getHierarchicalDataValue,
    getKeyIndicatorsResponse,
    getStaticIndicatorsResponse,
    getTrendingDataFn,
    getUploadingFiles,
    getWhatsNewResponse
} from './configuration.selector';
import { END, eventChannel } from 'redux-saga';
import { SAVE_CONFIGURATION_FAILED, SAVE_CONFIGURATION_SUCCESS } from '../config';
import { VIDEO_ALREADY_EXISTS } from 'common/config/message.config';

let emit: any;
const channel: any = eventChannel((emitter: any) => {
    emit = emitter;
    return () => {
        // unmounting function
    };
});

const getTrendingData = function* () {
    try {
        const ConfigurationTrendingApiResp:
            | ApiResult<ConfigurationIndicatorVM[] | [] | IErrorApiResponse>
            | undefined = yield ConfigurationService.getTrendingData();
        yield put({
            type: appStateActions.showLoader.type,
            payload: true
        });
        if (ConfigurationTrendingApiResp?.errors && ConfigurationTrendingApiResp?.hasErrors) {
            yield put({
                type: appStateActions.showLoader.type,
                payload: false
            });
            yield put({
                type: configurationActions.fetchTrendingDataFailed.type,
                payload: ConfigurationTrendingApiResp?.errors
            });
        } else {
            yield put({
                type: configurationActions.fetchTrendingDataSuccess.type,
                payload: ConfigurationTrendingApiResp?.value
            });
            yield put({
                type: appStateActions.showLoader.type,
                payload: false
            });
        }
    } catch (error: unknown) {
        yield put({
            type: appStateActions.showLoader.type,
            payload: false
        });
        if (axios.isAxiosError(error)) {
            if (error.response) {
                yield put({
                    type: configurationActions.fetchTrendingDataFailed.type,
                    payload: error.response.data
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: error.response.data,
                        type: MessageType.Error
                    }
                });
            } else if (error.request) {
                yield put({
                    type: configurationActions.fetchTrendingDataFailed.type,
                    payload: error.request
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: `Error in ${error.request}`,
                        type: MessageType.Error
                    }
                });
            }
        }
        if (error instanceof Error) {
            yield put({
                type: configurationActions.fetchTrendingDataFailed.type,
                payload: error.message
            });
            yield put({
                type: appStateActions.showMessageModal.type,
                payload: {
                    isVisible: true,
                    header: FAILURE_HEADER,
                    message: error.message,
                    type: MessageType.Error
                }
            });
        }
    }
};

const getTrendingDataWatch = function* () {
    yield takeLatest(configurationActions.fetchTrendingData.type, getTrendingData);
};

const getKeyIndicators = function* ({ payload }: PayloadAction<ConfigurationListRequest>) {
    const keyIndicatorResp: ApiResult<ConfigurationDetails | IErrorApiResponse> =
        yield ConfigurationService.getKeyIndicators(payload);
    if (keyIndicatorResp && keyIndicatorResp.hasErrors && keyIndicatorResp.errors) {
        yield put({
            type: configurationActions.fetchKeyIndicatorsFailed.type,
            payload: keyIndicatorResp.errors
        });
    } else {
        yield put({
            type: configurationActions.fetchKeyIndicatorsSuccess.type,
            payload: keyIndicatorResp.value
        });
    }
};

const getKeyIndicatorsWatch = function* () {
    yield takeLatest(configurationActions.fetchKeyIndicators.type, getKeyIndicators);
};

const getStaticIndicators = function* ({ payload }: PayloadAction<ConfigurationListRequest>) {
    const staticIndicatorsResp: ApiResult<ConfigurationDetails | IErrorApiResponse> =
        yield ConfigurationService.getStaticIndicators(payload);
    if (staticIndicatorsResp && staticIndicatorsResp.hasErrors && staticIndicatorsResp.errors) {
        yield put({
            type: configurationActions.fetchStaticIndicatorsFailed.type,
            payload: staticIndicatorsResp.errors
        });
    } else {
        yield put({
            type: configurationActions.fetchStaticIndicatorsSuccess.type,
            payload: staticIndicatorsResp.value
        });
    }
};

const getStaticIndicatorsWatch = function* () {
    yield takeLatest(configurationActions.fetchStaticIndicators.type, getStaticIndicators);
};

const deleteData = function* ({ payload }: PayloadAction<DeleteConfigurationRequest>) {
    const deleteResp: ApiResult<boolean | IErrorApiResponse> =
        yield ConfigurationService.deleteData(payload);
    if (deleteResp && deleteResp.hasErrors && deleteResp.errors) {
        yield put({
            type: configurationActions.deleteDataError.type,
            payload: deleteResp.errors
        });
    } else {
        yield put({
            type: configurationActions.deleteDataSuccess.type,
            payload: deleteResp.value
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: DELETE_POPUP_SUCCESS,
                message: DELETE_POPUP_MSG,
                type: MessageType.Success
            }
        });
    }
};

const deleteTrendingData = function* ({ payload: { id } }: PayloadAction<DeleteConfigRequestType>) {
    try {
        const apiPayload: DeleteConfigRequestType = { id };
        const ConfigurationDeleteTrendingApiResp:
            | ApiResult<boolean | IErrorApiResponse>
            | undefined = yield ConfigurationService.deleteTrendingData(apiPayload);

        if (
            ConfigurationDeleteTrendingApiResp?.errors &&
            ConfigurationDeleteTrendingApiResp?.hasErrors
        ) {
            yield put({
                type: configurationActions.deleteTrendingDataError.type,
                payload: ConfigurationDeleteTrendingApiResp?.errors
            });
        } else {
            if (ConfigurationDeleteTrendingApiResp?.value) {
                yield put({
                    type: configurationActions.deleteTrendingDataSuccess.type,
                    payload: ConfigurationDeleteTrendingApiResp?.value
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: SUCCESS_HEADER,
                        message: DELETE_POPUP_MSG,
                        type: MessageType.Success
                    }
                });
                yield put({ type: configurationActions.fetchTrendingData.type });
            } else {
                yield put({
                    type: configurationActions.deleteTrendingDataError.type,
                    payload: ConfigurationDeleteTrendingApiResp?.errors
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: DELETE_POPUP_FAILURE,
                        type: MessageType.Error
                    }
                });
            }
        }
    } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
            if (error.response) {
                yield put({
                    type: configurationActions.deleteTrendingDataError.type,
                    payload: error.response.data
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: error.response.data,
                        type: MessageType.Error
                    }
                });
            } else if (error.request) {
                yield put({
                    type: configurationActions.deleteTrendingDataError.type,
                    payload: error.request
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: `Error in ${error.request}`,
                        type: MessageType.Error
                    }
                });
            }
        }
        if (error instanceof Error) {
            yield put({
                type: configurationActions.deleteTrendingDataError.type,
                payload: error.message
            });
            yield put({
                type: appStateActions.showMessageModal.type,
                payload: {
                    isVisible: true,
                    header: FAILURE_HEADER,
                    message: error.message,
                    type: MessageType.Error
                }
            });
        }
    }
};
const deleteDataWatch = function* () {
    yield takeLatest(configurationActions.deleteData.type, deleteData);
};

const deleteWhatsNewData = function* ({
    payload
}: PayloadAction<ConfigurationWhatsNewDeleteRequest>) {
    const deleteWhatsNewResp: ApiResult<boolean | IErrorApiResponse> =
        yield ConfigurationService.deleteWhatsNewData(payload);
    if (deleteWhatsNewResp && deleteWhatsNewResp.hasErrors && deleteWhatsNewResp.errors) {
        yield put({
            type: configurationActions.deleteWhatsNewError.type,
            payload: deleteWhatsNewResp.errors
        });
    } else {
        yield put({
            type: configurationActions.deleteWhatsNewSuccess.type,
            payload: deleteWhatsNewResp.value
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: DELETE_POPUP_SUCCESS,
                message: DELETE_POPUP_MSG,
                type: MessageType.Success
            }
        });
    }
};

const deleteWhatsNewDataWatch = function* () {
    yield takeLatest(configurationActions.deleteWhatsNewData.type, deleteWhatsNewData);
};

const reorderData = function* ({ payload }: PayloadAction<ConfigurationReorderUpdateList[]>) {
    const reorderResp: ApiResult<boolean | IErrorApiResponse> =
        yield ConfigurationService.reorderData(payload);
    if (reorderResp && reorderResp.hasErrors && reorderResp.errors) {
        yield put({
            type: configurationActions.reOrderDataFailed.type,
            payload: reorderResp.errors
        });
    } else {
        yield put({
            type: configurationActions.reOrderDataSuccess.type,
            payload: reorderResp.value
        });
    }
};

const reorderTrendingData = function* ({
    payload: { reorderedData, data, newIndex }
}: PayloadAction<ReorderTrendingDataPayloadType>) {
    try {
        const ConfigurationReorderTrendingApiResp:
            | ApiResult<boolean | IErrorApiResponse>
            | undefined = yield ConfigurationService.reorderTrendingData(reorderedData);

        if (
            ConfigurationReorderTrendingApiResp?.errors &&
            ConfigurationReorderTrendingApiResp?.hasErrors
        ) {
            yield put({
                type: configurationActions.reorderTrendingDataFailed.type,
                payload: ConfigurationReorderTrendingApiResp?.errors
            });
        } else {
            if (ConfigurationReorderTrendingApiResp?.value) {
                yield put({
                    type: configurationActions.reorderTrendingDataSuccess.type,
                    payload: { data, newIndex }
                });
            } else {
                yield put({
                    type: configurationActions.reorderTrendingDataFailed.type,
                    payload: ConfigurationReorderTrendingApiResp?.errors
                });
            }
        }
    } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
            if (error.response) {
                yield put({
                    type: configurationActions.reorderTrendingDataFailed.type,
                    payload: error.response.data
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: error.response.data,
                        type: MessageType.Error
                    }
                });
            } else if (error.request) {
                yield put({
                    type: configurationActions.reorderTrendingDataFailed.type,
                    payload: error.request
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: `Error in ${error.request}`,
                        type: MessageType.Error
                    }
                });
            }
        }
        if (error instanceof Error) {
            yield put({
                type: configurationActions.reorderTrendingDataFailed.type,
                payload: error.message
            });
            yield put({
                type: appStateActions.showMessageModal.type,
                payload: {
                    isVisible: true,
                    header: FAILURE_HEADER,
                    message: error.message,
                    type: MessageType.Error
                }
            });
        }
    }
};

const reorderTrendingDataWatch = function* () {
    yield takeLatest(configurationActions.reorderTrendingData.type, reorderTrendingData);
};

const reorderDataWatch = function* () {
    yield takeLatest(configurationActions.reOrderData.type, reorderData);
};

const reorderWhatsNew = function* ({ payload }: PayloadAction<ConfigurationReorderUpdateList[]>) {
    const reorderWhatsNewResp: ApiResult<boolean | IErrorApiResponse> =
        yield ConfigurationService.reorderWhatsNewConfiguration(payload);
    if (reorderWhatsNewResp && reorderWhatsNewResp.hasErrors && reorderWhatsNewResp.errors) {
        yield put({
            type: configurationActions.reOrderWhatsNewFailed.type,
            payload: reorderWhatsNewResp.errors
        });
    } else {
        yield put({
            type: configurationActions.reOrderWhatsNewSuccess.type,
            payload: reorderWhatsNewResp.value
        });
    }
};

const reorderWhatsNewWatch = function* () {
    yield takeLatest(configurationActions.reOrderWhatsNew.type, reorderWhatsNew);
};

const fetchFrequentlyUsed = function* ({ payload }: PayloadAction<ConfigurationListRequest>) {
    const frequentlyUsedResp: ApiResult<ConfigurationDetails | IErrorApiResponse> =
        yield ConfigurationService.getFrequentlyUsed(payload);
    if (frequentlyUsedResp && frequentlyUsedResp.hasErrors && frequentlyUsedResp.errors) {
        yield put({
            type: configurationActions.fetchFrequentlyUsedFailed.type,
            payload: frequentlyUsedResp.errors
        });
    } else {
        yield put({
            type: configurationActions.fetchFrequentlyUsedSuccess.type,
            payload: frequentlyUsedResp.value
        });
    }
};

const fetchFrequentlyUsedWatch = function* () {
    yield takeLatest(configurationActions.fetchFrequentlyUsed.type, fetchFrequentlyUsed);
};

const fetchGeographyOverview = function* ({ payload }: PayloadAction<ConfigurationListRequest>) {
    const geographyOverviewResp: ApiResult<ConfigurationDetails | IErrorApiResponse> =
        yield ConfigurationService.getGeographyOverview(payload);
    if (geographyOverviewResp && geographyOverviewResp.hasErrors && geographyOverviewResp.errors) {
        yield put({
            type: configurationActions.fetchGeographyOverviewFailed.type,
            payload: geographyOverviewResp.errors
        });
    } else {
        yield put({
            type: configurationActions.fetchGeographyOverviewSuccess.type,
            payload: geographyOverviewResp.value
        });
    }
};

const fetchGeographyOverviewWatch = function* () {
    yield takeLatest(configurationActions.fetchGeographyOverview.type, fetchGeographyOverview);
};

const fetchWhatsNew = function* () {
    const whatsNewResp: ApiResult<ConfigurationWhatsNewVM[] | IErrorApiResponse> =
        yield ConfigurationService.fetchWhatsNew();
    if (whatsNewResp && whatsNewResp.hasErrors && whatsNewResp.errors) {
        yield put({
            type: configurationActions.fetchWhatsNewFailed.type,
            payload: whatsNewResp.errors
        });
    } else {
        yield put({
            type: configurationActions.fetchWhatsNewSuccess.type,
            payload: whatsNewResp.value
        });
    }
};

const fetchWhatsNewWatch = function* () {
    yield takeLatest(configurationActions.fetchWhatsNew.type, fetchWhatsNew);
};

const saveIndicatorAdd = function* ({
    payload
}: PayloadAction<ConfigurationSaveRequest<ConfigurationTypes>>) {
    const saveIndicatorResp: ApiResult<boolean | IErrorApiResponse> =
        yield ConfigurationService.saveIndicatorAdd(payload);
    if (saveIndicatorResp && saveIndicatorResp.hasErrors && saveIndicatorResp.errors) {
        yield put({
            type: configurationActions.saveIndicatorAddFailed.type,
            payload: saveIndicatorResp.errors
        });
    } else {
        yield put({
            type: configurationActions.saveIndicatorAddSuccess.type,
            payload: saveIndicatorResp.value
        });
    }
};

const saveIndicatorAddWatch = function* () {
    yield takeLatest(configurationActions.saveIndicatorAdd.type, saveIndicatorAdd);
};

const saveWhatsNewIndicator = function* ({
    payload
}: PayloadAction<WhatsNewIndicatorSaveRequest<WhatsNewLinkType>>) {
    const saveWhatsNewIndicatorResp: ApiResult<boolean | IErrorApiResponse> =
        yield ConfigurationService.saveWhatsNewIndicator(payload);
    if (
        saveWhatsNewIndicatorResp &&
        saveWhatsNewIndicatorResp.hasErrors &&
        saveWhatsNewIndicatorResp.errors
    ) {
        yield put({
            type: configurationActions.saveWhatsNewIndicatorFailed.type,
            payload: saveWhatsNewIndicatorResp.errors
        });
    } else {
        yield put({
            type: configurationActions.saveWhatsNewIndicatorSuccess.type,
            payload: saveWhatsNewIndicatorResp.value
        });
    }
};

const saveWhatsNewIndicatorWatch = function* () {
    yield takeLatest(configurationActions.saveWhatsNewIndicator.type, saveWhatsNewIndicator);
};

const saveTrendingData = function* ({ payload }: PayloadAction<TrendingDataSaveRequest>) {
    const saveTrendingDataResp: ApiResult<boolean | IErrorApiResponse> =
        yield ConfigurationService.saveTrendingData(payload);
    if (saveTrendingDataResp && saveTrendingDataResp.hasErrors && saveTrendingDataResp.errors) {
        yield put({
            type: configurationActions.saveTrendingDataFailed.type,
            payload: saveTrendingDataResp.errors
        });
    } else {
        yield put({
            type: configurationActions.saveTrendingDataSuccess.type,
            payload: saveTrendingDataResp.value
        });
    }
};

const saveTrendingDataWatch = function* () {
    yield takeLatest(configurationActions.saveTrendingData.type, saveTrendingData);
};

const fetchUploadFileLog = function* (action: PayloadAction<string>) {
    if (action.payload) {
        const result: ApiResult<PaginatedApiResponse<FileUploadLogDataVM[]>> =
            yield ConfigurationService.getUploadFileLog(action.payload);
        if (result) {
            yield put(configurationActions.fetchUploadFileLogSuccess(result.value!));
        } else {
            yield put(configurationActions.fetchUploadFileLogError());
        }
    }
};

const fetchUploadFileLogWatch = function* () {
    yield takeLatest(configurationActions.fetchUploadFileLogBegin.type, fetchUploadFileLog);
};

const deleteTrendingDataWatch = function* () {
    yield takeLatest(configurationActions.deleteTrendingData.type, deleteTrendingData);
};

const getCategories = function* ({ payload }: PayloadAction<PagedRequest>) {
    try {
        const masterdataGetCategoriesApiResp:
            | ApiResult<PaginatedApiResponse<MasterDataEntity[] | [] | IErrorApiResponse>>
            | undefined = yield ConfigurationService.getCategories(payload);

        if (masterdataGetCategoriesApiResp?.errors && masterdataGetCategoriesApiResp?.hasErrors) {
            yield put({
                type: configurationActions.getCategoriesFailed.type,
                payload: masterdataGetCategoriesApiResp?.errors
            });
        } else {
            yield put({
                type: configurationActions.getCategoriesSuccess.type,
                payload: masterdataGetCategoriesApiResp?.value
            });
        }
    } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
            if (error.response) {
                yield put({
                    type: configurationActions.getCategoriesFailed.type,
                    payload: error.response.data
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: error.response.data,
                        type: MessageType.Error
                    }
                });
            } else if (error.request) {
                yield put({
                    type: configurationActions.getCategoriesFailed.type,
                    payload: error.request
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: `Error in ${error.request}`,
                        type: MessageType.Error
                    }
                });
            }
        }
        if (error instanceof Error) {
            yield put({
                type: configurationActions.getCategoriesFailed.type,
                payload: error.message
            });
            yield put({
                type: appStateActions.showMessageModal.type,
                payload: {
                    isVisible: true,
                    header: FAILURE_HEADER,
                    message: error.message,
                    type: MessageType.Error
                }
            });
        }
    }
};

const getCategoriesWatch = function* () {
    yield takeLatest(configurationActions.getCategories, getCategories);
};

const getSelector = function (
    configurationType: ConfigurationTypes
): null | ((state: RootState) => IAsyncData<ConfigurationIndicatorVM[] | [] | IErrorApiResponse>) {
    const configurationData: ConfigurationDataModifier = { selector: null };
    switch (configurationType) {
        case ConfigurationTypes.TrendingData:
            configurationData.selector = getTrendingDataFn;
            break;
        case ConfigurationTypes.FrequentlyUsed:
            configurationData.selector = getFrequentlyUsedResponse;
            break;
        case ConfigurationTypes.GeographyOverview:
            configurationData.selector = getGeographyOverviewResponse;
            break;
        case ConfigurationTypes.KeyIndicator:
            configurationData.selector = getKeyIndicatorsResponse;
            break;
        case ConfigurationTypes.StaticIndicator:
            configurationData.selector = getStaticIndicatorsResponse;
            break;
        case ConfigurationTypes.WhatsNew:
            configurationData.selector = getWhatsNewResponse;
            break;
        default:
            break;
    }
    return configurationData.selector;
};

const getEntitiesByCategoryHelper = function* (payload: HierarchicalExpansionPayload<EntityVM>) {
    const { data, configurationType } = payload;
    const hierarchicalData: Entity[] = yield select(getHierarchicalDataValue);
    let subCats: any = {};
    if (data.entityType === EntityType.Category) {
        subCats = findCategories(hierarchicalData, data.id);
    } else {
        subCats = findCategoriesByHierarchyId(hierarchicalData, data.hierarchyId);
    }
    if (!data.isExpanded && !hasValue(subCats.subCategories)) {
        let categories: ApiResult<EntityVM[] | IErrorApiResponse>;
        if (data.entityType === EntityType.Category)
            categories = yield ConfigurationService.getEntitiesByCategoryId(data.id);
        else categories = yield ConfigurationService.getEntitiesByHierarchyId(data.hierarchyId);

        if (categories?.hasErrors && categories?.errors) {
            yield put({
                type: configurationActions.toggleHierarchicalExpansionFailed.type,
                payload: categories?.errors
            });
        } else {
            const configurationSelector:
                | null
                | ((
                      state: RootState
                  ) => ApiResult<ConfigurationIndicatorVM[] | [] | IErrorApiResponse>) = yield call(
                getSelector,
                configurationType
            );
            if (configurationSelector !== null) {
                const configurationList: IAsyncData<ConfigurationIndicatorVM[]> = yield select(
                    configurationSelector
                );
                if (categories.value && Array.isArray(categories.value) && configurationList.data) {
                    categories.value = yield call(
                        mapIndicators,
                        categories?.value,
                        configurationList.data
                    );
                }
            }
            yield put({
                type: configurationActions.toggleHierarchicalExpansionSuccess.type,
                payload: {
                    parent: data,
                    subCategories: categories?.value
                }
            });
        }
    } else {
        yield put({
            type: configurationActions.toggleHierarchicalDataCollapse.type
        });
    }
};

const getEntitiesByCategory = function* ({
    payload
}: PayloadAction<HierarchicalExpansionPayload<EntityVM>>) {
    try {
        yield call(getEntitiesByCategoryHelper, payload);
    } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
            if (error.response) {
                yield put({
                    type: configurationActions.toggleHierarchicalExpansionFailed.type,
                    payload: error.response.data
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: error.response.data,
                        type: MessageType.Error
                    }
                });
            } else if (error.request) {
                yield put({
                    type: configurationActions.toggleHierarchicalExpansionFailed.type,
                    payload: error.request
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: `Error in ${error.request}`,
                        type: MessageType.Error
                    }
                });
            }
        }
        if (error instanceof Error) {
            yield put({
                type: configurationActions.toggleHierarchicalExpansionFailed.type,
                payload: error.message
            });
            yield put({
                type: appStateActions.showMessageModal.type,
                payload: {
                    isVisible: true,
                    header: FAILURE_HEADER,
                    message: error.message,
                    type: MessageType.Error
                }
            });
        }
    }
};

const getHierarchicalDataByCategoryWatch = function* () {
    yield takeLatest(
        configurationActions.toggleHierarchicalDataExpansion.type,
        getEntitiesByCategory
    );
};

const uploadFile = function* ({ payload }: PayloadAction<ConfigurationUploadFileRequest>) {
    const onProgress = (event: AxiosProgressEvent) => {
        if (event.total) {
            const percentCompleted = Math.round((event.loaded / event.total) * 100);
            emit(percentCompleted);
            if (percentCompleted == 100) emit(END);
            else emit(percentCompleted);
        }
    };
    try {
        const { hasErrors, errors, value }: ApiResult<boolean | IErrorApiResponse> =
            yield ConfigurationService.uploadFile(
                payload.whatsNewTypes,
                payload.files,
                onProgress,
                payload.id,
                payload.name,
                payload.description,
                payload.existingFileReplaced
            );
        if (hasErrors && errors) {
            const uploadError = errors[0];
            yield put({
                type: configurationActions.uploadFileFailed.type,
                payload: uploadError
            });
            yield put({
                type: appStateActions.showMessageModal.type,
                payload: {
                    isVisible: true,
                    header: FAILURE_HEADER,
                    message: uploadError,
                    type: MessageType.Error
                }
            });
        } else {
            if (value) {
                yield put({
                    type: configurationActions.uploadFileSuccess.type,
                    payload: FILE_UPLOAD_SUCCESS_MESSAGE
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: SUCCESS_HEADER,
                        message: FILE_UPLOAD_SUCCESS_MESSAGE,
                        type: MessageType.Success
                    }
                });
            } else {
                yield put({
                    type: configurationActions.uploadFileFailed.type,
                    payload: FILE_UPLOAD_ERROR_MESSAGE
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: FILE_UPLOAD_ERROR_MESSAGE,
                        type: MessageType.Error
                    }
                });
            }
        }
    } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
            if (error.response) {
                yield put({
                    type: configurationActions.fetchTrendingDataFailed.type,
                    payload: error.response.data
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: error.response.data,
                        type: MessageType.Error
                    }
                });
            } else if (error.request) {
                yield put({
                    type: configurationActions.fetchTrendingDataFailed.type,
                    payload: error.request
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: `Error in ${error.request}`,
                        type: MessageType.Error
                    }
                });
            }
        }
        if (error instanceof Error) {
            yield put({
                type: configurationActions.fetchTrendingDataFailed.type,
                payload: error.message
            });
            yield put({
                type: appStateActions.showMessageModal.type,
                payload: {
                    isVisible: true,
                    header: FAILURE_HEADER,
                    message: error.message,
                    type: MessageType.Error
                }
            });
        }
    }
};

const uploadFileFileWatch = function* () {
    yield takeLatest(configurationActions.uploadFile.type, uploadFile);
};

const uploadProgressWatch = function* (
    evtChannel: any = channel
): Generator<any, void, number | RootState | any> {
    while (true) {
        const data = yield take(evtChannel);
        // const state = yield select(getUploadingFiles);
        const uploadingFiles = yield select(getUploadingFiles);
        const updatedFile: FileDetails = new File([uploadingFiles[0]], uploadingFiles[0].name, {
            type: uploadingFiles[0].type
        });
        updatedFile.progress = data;
        const newUploadingFiles = [updatedFile];
        yield put({
            type: configurationActions.setUploadingFiles.type,
            payload: newUploadingFiles
        });
    }
};

const saveWhatsNewLink = function* (param: PayloadAction<ConfigurationSaveLinkRequest>) {
    try {
        const { errors, hasErrors, value }: ApiResult<boolean | IErrorApiResponse> =
            yield ConfigurationService.saveWhatsNewLink(param);

        if (errors && hasErrors) {
            yield put({
                type: configurationActions.saveWhatsNewLinkFailed.type,
                payload: errors
            });
        } else {
            if (value) {
                yield put({
                    type: configurationActions.saveWhatsNewLinkSuccess.type,
                    payload: value
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: SUCCESS_HEADER,
                        message: SAVE_CONFIGURATION_SUCCESS,
                        type: MessageType.Success
                    }
                });
            } else
                yield put({
                    type: configurationActions.saveWhatsNewLinkFailed.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: SAVE_CONFIGURATION_FAILED,
                        type: MessageType.Error
                    }
                });
        }
    } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
            if (error.response) {
                yield put({
                    type: configurationActions.saveWhatsNewLinkFailed.type,
                    payload: error.response.data
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: error.response.data,
                        type: MessageType.Error
                    }
                });
            } else if (error.request) {
                yield put({
                    type: configurationActions.saveWhatsNewLinkFailed.type,
                    payload: error.request
                });
                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: `Error in ${error.request}`,
                        type: MessageType.Error
                    }
                });
            }
        }
        if (error instanceof Error) {
            yield put({
                type: configurationActions.saveWhatsNewLinkFailed.type,
                payload: error.message
            });
            yield put({
                type: appStateActions.showMessageModal.type,
                payload: {
                    isVisible: true,
                    header: FAILURE_HEADER,
                    message: error.message,
                    type: MessageType.Error
                }
            });
        }
    }
};
const saveWhatsNewLinkWatch = function* () {
    yield takeLatest(configurationActions.saveWhatsNewLink.type, saveWhatsNewLink);
};

const getIndicatorSources = function* ({ payload }: PayloadAction<PagedRequest>) {
    const { hasErrors, errors, value } = yield ConfigurationService.getIndicatorSources(payload);
    if (hasErrors && errors) {
        yield put({
            type: configurationActions.fetchIndicatorSourcesFailed.type,
            payload: value && value.detail
        });
    } else {
        yield put({
            type: configurationActions.fetchIndicatorSourcesSuccess.type,
            payload: value
        });
    }
};

const getIndicatorSourcesWatch = function* () {
    yield takeLatest(configurationActions.fetchIndicatorSources.type, getIndicatorSources);
};

const addIndicatorSource = function* ({ payload }: PayloadAction<IndicatorSourceDTO>) {
    const { hasErrors, errors, value }: ApiResult<any> =
        yield ConfigurationService.addIndicatorSource(payload);
    if (hasErrors && errors) {
        yield put({
            type: configurationActions.addIndicatorSourceFailed.type,
            payload: value
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value && value.detail ? value.detail : INDICATOR_SOURCE_ADD_FAIL,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: configurationActions.addIndicatorSourceSuccess.type,
            payload: value
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: INDICATOR_SOURCE_ADD_SUCCESS,
                type: MessageType.Success
            }
        });
        yield put({
            type: configurationActions.fetchIndicatorSources.type,
            payload: {
                pageNumber: PAGE_NUMBER_DEFAULT,
                pageSize: PAGE_SIZE_DEFAULT,
                searchText: ''
            }
        });
    }
};

const addIndicatorSourceWatch = function* () {
    yield takeLatest(configurationActions.addIndicatorSource.type, addIndicatorSource);
};

const updateIndicatorSource = function* ({ payload }: PayloadAction<IndicatorSourceDTO>) {
    const { hasErrors, errors, value }: ApiResult<any> =
        yield ConfigurationService.updateIndicatorSource(payload);
    if (hasErrors && errors) {
        yield put({
            type: configurationActions.updateIndicatorSourceFailed.type,
            payload: value
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value && value.detail ? value.detail : INDICATOR_SOURCE_EDIT_FAIL,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: configurationActions.updateIndicatorSourceSuccess.type,
            payload: payload
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: INDICATOR_SOURCE_EDIT_SUCCESS,
                type: MessageType.Success
            }
        });
    }
};

const updateIndicatorSourceWatch = function* () {
    yield takeLatest(configurationActions.updateIndicatorSource.type, updateIndicatorSource);
};

const deleteIndicatorSource = function* ({ payload }: PayloadAction<IndicatorSourceDTO>) {
    const { hasErrors, errors, value }: ApiResult<any> =
        yield ConfigurationService.deleteIndicatorSource(payload);
    if (hasErrors && errors) {
        yield put({
            type: configurationActions.deleteIndicatorSourceFailed.type,
            payload: value
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value && value.detail ? value.detail : INDICATOR_SOURCE_DELETE_FAIL,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: configurationActions.deleteIndicatorSourceSuccess.type,
            payload: payload
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: INDICATOR_SOURCE_DELETE_SUCCESS,
                type: MessageType.Success
            }
        });
    }
};

const deleteIndicatorSourceWatch = function* () {
    yield takeLatest(configurationActions.deleteIndicatorSource.type, deleteIndicatorSource);
};

const saveVideoLinkAdd = function* ({
    payload: { payload, callBack }
}: PayloadAction<{ payload: VideoDetailsPayload; callBack: (param: boolean) => void }>) {
    const saveVideoResp: ApiResult<VideoDetailResponse> =
        yield ConfigurationService.saveVideoLinkAdd(payload);
    if (saveVideoResp?.hasErrors && saveVideoResp.errors) {
        yield put({
            type: configurationActions.saveVideoLinkFailed.type,
            payload: saveVideoResp.errors
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: DEFAULT_FAILURE_SAVE_MESSAGE,
                type: MessageType.Error
            }
        });
    } else if (saveVideoResp?.value?.isSaved && !saveVideoResp.value?.isExist) {
        yield put({
            type: configurationActions.saveVideoLinkSuccess.type,
            payload: saveVideoResp.value
        });
        callBack(true);
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: DEFAULT_SAVE_MESSAGE,
                type: MessageType.Success
            }
        });
    } else if (saveVideoResp && !saveVideoResp.value?.isSaved && saveVideoResp.value?.isExist) {
        yield put({
            type: configurationActions.saveVideoLinkFailed.type,
            payload: saveVideoResp.errors
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: VIDEO_ALREADY_EXISTS,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: configurationActions.saveVideoLinkFailed.type,
            payload: saveVideoResp.value
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: DEFAULT_FAILURE_SAVE_MESSAGE,
                type: MessageType.Error
            }
        });
    }
};

const updateVideoLinkAdd = function* ({
    payload: { payload, callBack }
}: PayloadAction<{ payload: VideoDetailsPayload; callBack: (param: boolean) => void }>) {
    const updateVideoResp: ApiResult<VideoDetailResponse> =
        yield ConfigurationService.updateVideoLinkAdd(payload);
    if (updateVideoResp?.hasErrors && updateVideoResp.errors) {
        yield put({
            type: configurationActions.updateVideoLinkFailed.type,
            payload: updateVideoResp.errors
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: DEFAULT_FAILURE_SAVE_MESSAGE,
                type: MessageType.Error
            }
        });
    } else if (updateVideoResp?.value?.isSaved && !updateVideoResp.value?.isExist) {
        yield put({
            type: configurationActions.updateVideoLinkSuccess.type,
            payload: updateVideoResp.value
        });
        callBack(true);
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: DEFAULT_SAVE_MESSAGE,
                type: MessageType.Success
            }
        });
    } else if (
        updateVideoResp &&
        !updateVideoResp.value?.isSaved &&
        updateVideoResp.value?.isExist
    ) {
        yield put({
            type: configurationActions.updateVideoLinkFailed.type,
            payload: updateVideoResp.errors
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: VIDEO_ALREADY_EXISTS,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: configurationActions.updateVideoLinkFailed.type,
            payload: updateVideoResp.value
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: DEFAULT_FAILURE_SAVE_MESSAGE,
                type: MessageType.Error
            }
        });
    }
};

const deleteVideoLinkWatch = function* () {
    yield takeLatest(configurationActions.deleteVideoLink.type, deleteVideoLink);
};

const deleteVideoLink = function* ({ payload }: PayloadAction<DeleteVideoLinkPayload>) {
    const deleteVideoResponse: ApiResult<boolean | IErrorApiResponse> =
        yield ConfigurationService.deleteVideoLink(payload);
    if (deleteVideoResponse?.hasErrors && deleteVideoResponse.errors) {
        yield put({
            type: configurationActions.deleteVideoLinkFailed.type,
            payload: deleteVideoResponse.errors
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: DELETE_POPUP_FAILURE,
                type: MessageType.Success
            }
        });
    } else if (deleteVideoResponse?.value) {
        yield put({
            type: configurationActions.deleteVideoLinkSuccess.type,
            payload
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: DELETE_POPUP_MSG,
                type: MessageType.Success
            }
        });
    } else {
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: DELETE_POPUP_FAILURE,
                type: MessageType.Success
            }
        });
    }
};

const saveVideoLinkAddWatch = function* () {
    yield takeLatest(configurationActions.saveVideoLink.type, saveVideoLinkAdd);
};
const updateVideoLinkAddWatch = function* () {
    yield takeLatest(configurationActions.updateVideoLink.type, updateVideoLinkAdd);
};

const fetchVideoLink = function* () {
    const videoLinkResp: ApiResult<any | IErrorApiResponse> =
        yield ConfigurationService.getVideoLink();
    if (videoLinkResp && videoLinkResp?.hasErrors && videoLinkResp.errors) {
        yield put({
            type: configurationActions.fetchVideoLinkFailed.type,
            payload: videoLinkResp.errors
        });
    } else {
        yield put({
            type: configurationActions.fetchVideoLinkSuccess.type,
            payload: videoLinkResp.value
        });
    }
};

const fetchVideoLinkWatch = function* () {
    yield takeLatest(configurationActions.fetchVideoLink.type, fetchVideoLink);
};

export {
    getTrendingDataWatch,
    getKeyIndicatorsWatch,
    getStaticIndicatorsWatch,
    deleteDataWatch,
    fetchFrequentlyUsedWatch,
    fetchWhatsNewWatch,
    saveIndicatorAddWatch,
    fetchGeographyOverviewWatch,
    fetchUploadFileLogWatch,
    reorderDataWatch,
    deleteTrendingDataWatch,
    reorderTrendingDataWatch,
    getCategoriesWatch,
    getHierarchicalDataByCategoryWatch,
    saveTrendingDataWatch,
    uploadFileFileWatch,
    uploadProgressWatch,
    deleteWhatsNewDataWatch,
    saveWhatsNewIndicatorWatch,
    reorderWhatsNewWatch,
    saveWhatsNewLinkWatch,
    getIndicatorSourcesWatch,
    addIndicatorSourceWatch,
    updateIndicatorSourceWatch,
    deleteIndicatorSourceWatch,
    saveVideoLinkAddWatch,
    updateVideoLinkAddWatch,
    fetchVideoLinkWatch,
    deleteVideoLinkWatch
};
